import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { Emoji } from "emoji-mart"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { Button, Container, Label, Message, Popup } from "semantic-ui-react"
import SearchParams from "simplydo/core/searchParams"
import styled, { ThemeContext } from "styled-components"

import api, { asQuery } from "api"
import IdeaBody from "components/ideas/Body"
import { LaneInfo } from "components/ideas/Header"
import { useAssessmentEditor } from "components/ideas/hooks"
import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips"
import { HoverAction, HoverActions } from "components/lib/HoverActions"
import SlidingModal from "components/lib/SlidingModal"
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI"
import { useAppSelector } from "store"
import util from "utils/utils"

import PreviewSideWindow from "./PreviewSideWindow/PreviewSideWindow"

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`

const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f2f2f3;
`

const PreviewHeader = styled.div<{ $coverImage?: string }>`
  width: 100%;
  position: relative;

  .overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.21);
    z-index: 11;
  }
  .overlay-body {
    position: relative;
    z-index: 12;
    display: flex;
    flex-direction: column;
    padding: 10px;
  }
  top: 0;
  left: 0;
  ${({ $coverImage }) =>
    $coverImage
      ? `
    background-image: url(${$coverImage});
    background-size:cover;
    background-position: center center;
  `
      : `
    background-color: #e9ebee;
  `}
  border-bottom: 1px solid lightgray;
  z-index: 999;
  min-height: 175px;
  overflow: hidden;
  text-overflow: ellipsis;
  .ui.button {
    border: 0.5px solid gray;
  }
  h1 {
    font-size: 2.3em;
    margin-top: 20px;
    margin-bottom: 10px;
    color: white;
    line-height: 1.15em;
  }
`

const IdeaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 13;
  background-color: #f2f2f3;
`

const BodyContainer = styled.div`
  width: 100%;
  padding: 10px;
`

const FlexRow = styled.div<{ $wrap?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ $wrap }) =>
    $wrap &&
    `
    flex-wrap: wrap;
    margin-top: -7.5px;
    > * {
      margin-top: 7.5px;
    }
  `}
`

const IdeaPreview = (props) => {
  const {
    isOpen,

    idea,
    loading,
    challenge: propChallenge,

    closeModal: propCloseModal = () => {},

    onAssessmentEdited,
    moveIdea,
    canGoNextIdea,
    canGoPrevIdea,
    assessmentEnabled,
    onUpdateIdea,
    onIdeaActivity,
    updateProjectLane,
    lanes,
    removeIdeaFromMerge,
    removingIdeaFromMerge,
    currentIdeaPosition,
    totalIdeas,
    redirectToBoard,
    page,
    limit,
  } = props
  const user = useAppSelector((state) => state.user)
  const navigate = useNavigate()
  const previewContainerRef = useRef(null)
  const [modalIdeaWidth, setModalIdeaWidth] = useState(0)

  const sectionRefs = useRef({})
  const setSectionRef = useCallback((ref, itemId) => {
    sectionRefs.current[itemId] = ref
  }, [])
  const fieldRefs = useRef({})
  const setFieldRef = useCallback((ref, itemId) => {
    fieldRefs.current[itemId] = ref
  }, [])

  const { t } = useTranslation()

  const theme = useContext(ThemeContext)
  const [previewInitialised, setPreviewInitialised] = useState(false)
  const challengeId = idea?.challenge
  const board = useMemo(
    () => idea?.projectManagement?.boards?.find((b) => b.forId === challengeId),
    [idea, challengeId],
  )
  const ideaId = idea?._id

  const { assessmentState, updateAssessment, submitAssessment } = useAssessmentEditor(idea, onAssessmentEdited)
  const { assessment, assessmentIsSubmissable } = assessmentState

  // only show pm board management actions if the user can manage challenge (collaborator/owner/admins) or is assigned to the idea
  const canManageChallenge = util.canManageChallenge(user, propChallenge ?? idea?.ownerChallenge)
  const canViewProjectBoard =
    canManageChallenge ||
    (board?.assignees?.indexOf(user._id) > -1 && idea?.ownerChallenge?.projectManagementVisibility === "users") ||
    util.hasPermission(user, "challenge.viewProjectBoard", challengeId)

  const closeModal = useCallback(() => {
    propCloseModal()
  }, [propCloseModal])

  const updateQueryParams = useCallback(() => {
    const currQueryParams = new SearchParams(window.location.search)
    const currQueryParamsObject = Object.fromEntries(currQueryParams as any)
    const shouldIncludeIdeaId = !!ideaId
    const { idea: _, ...otherQueryParams } = currQueryParamsObject
    const newQueryParams = shouldIncludeIdeaId ? { ...otherQueryParams, idea: ideaId } : otherQueryParams

    const queryParams = new SearchParams(newQueryParams).toString()
    navigate({ search: `?${queryParams}` }, { replace: true })
  }, [ideaId, navigate])

  const onOpenPreview = useCallback(() => {
    updateQueryParams()
    setModalIdeaWidth(previewContainerRef.current?.clientWidth ?? 0)
  }, [updateQueryParams])

  // To prevent the preview immediately resetting the query params to nothing, we only set the query parameters once the preview has been open ONCE
  // It's then permanently allowed to be edited
  useEffect(() => {
    if (idea) {
      setPreviewInitialised((prev) => prev || true)
    }
  }, [idea])

  useEffect(() => {
    if (previewInitialised) {
      updateQueryParams()
    }
  }, [updateQueryParams, previewInitialised])

  const updateIdea = useCallback(
    (data, fieldPath) => {
      const updatedIdea = { ...idea }
      let current = updatedIdea
      for (let i = 0; i < fieldPath.length - 1; i++) {
        const key = fieldPath[i]
        if (!current[key]) {
          current[key] = {}
        }
        current = current[key]
      }
      current[fieldPath[fieldPath.length - 1]] = data
      onUpdateIdea(updatedIdea)
    },
    [idea, onUpdateIdea],
  )

  return (
    <SlidingModal
      isOpen={isOpen}
      onOpen={onOpenPreview}
      onClose={() => closeModal()}
      width={theme?.sizes.isComputer ? "60%" : "100%"}
      ref={previewContainerRef}
    >
      <ModalContainer>
        {theme.sizes.isComputer && idea && !loading ? (
          <HoverActions $width={modalIdeaWidth}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <HoverAction icon="close" onClick={() => closeModal()}>
                Close
              </HoverAction>
              {moveIdea && isOpen && (canGoPrevIdea || canGoNextIdea) ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <Popup
                    on="hover"
                    trigger={
                      <div>
                        <HoverAction
                          icon="arrow left"
                          onClick={() => moveIdea(-1)}
                          margined={!(currentIdeaPosition && totalIdeas)}
                          disabled={!canGoPrevIdea}
                        />
                      </div>
                    }
                    content={`Go to previous ${t("generic.idea")}`}
                  />

                  {currentIdeaPosition && totalIdeas && (canGoNextIdea || canGoPrevIdea) && (
                    <HoverAction disabled>
                      {t("common:capitalise", { key: "generic.idea" })} {(page - 1) * limit + currentIdeaPosition} of{" "}
                      {totalIdeas}
                    </HoverAction>
                  )}
                  <Popup
                    trigger={
                      <div>
                        <HoverAction icon="arrow right" onClick={() => moveIdea(1)} disabled={!canGoNextIdea} />
                      </div>
                    }
                    content={`Go to next ${t("generic.idea")}`}
                  />
                </div>
              ) : null}
            </div>

            <div className="hover-actions-inner">
              <PreviewSideWindow
                idea={idea}
                assessment={assessment}
                onUpdateIdea={onUpdateIdea}
                assessmentEnabled={assessmentEnabled}
                lanes={lanes}
                updateProjectLane={updateProjectLane}
                onIdeaActivity={onIdeaActivity}
                challenge={propChallenge}
                sectionRefs={sectionRefs}
                fieldRefs={fieldRefs}
                scrollRef={previewContainerRef}
              />
            </div>
          </HoverActions>
        ) : null}
        <PreviewContainer>
          {!loading && !idea ? (
            <Container style={{ height: "100vh" }}>
              <Message error style={{ margin: "2rem 0" }}>
                <Message.Header>Unable to load {t("generic.idea")}</Message.Header>
                <p>Sorry, we were unable to load this {t("generic.idea")}. Please try again later.</p>
              </Message>
              <Button content={`Close`} icon="chevron left" onClick={() => closeModal()} />
            </Container>
          ) : (
            <>
              <PreviewHeader $coverImage={util.ideaCoverImage(idea, "2560x400")}>
                <div className="overlay" />
                <div className="overlay-body">
                  <FlexRow>
                    <ModalContainer style={{ justifyContent: "flex-end" }}>
                      {!loading ? (
                        <>
                          <Button
                            primary
                            size="tiny"
                            content={`Open full ${t("generic.idea")}`}
                            icon="file alternate"
                            as={Link}
                            to={`/ideas/${ideaId}`}
                          />
                          {redirectToBoard && (
                            <Button
                              primary
                              size="tiny"
                              content={`View in project board`}
                              icon="file alternate"
                              as={Link}
                              to={`/challenges/${challengeId}/board?idea=${ideaId}`}
                            />
                          )}
                          {!theme.sizes.isComputer ? (
                            <Button size="tiny" content={`Close preview`} onClick={() => closeModal()} />
                          ) : null}
                        </>
                      ) : null}
                    </ModalContainer>
                  </FlexRow>
                  <div style={{ paddingLeft: 10 }}>
                    <div style={{ marginTop: 10 }}>
                      {loading ? (
                        <PlaceholderStack gap={20} style={{ width: 400 }}>
                          <PlaceholderItem height={40} style={{ width: 400 }} />
                          <PlaceholderItem height={25} style={{ width: 200 }} />
                          <PlaceholderItem height={30} style={{ width: 200 }} />
                        </PlaceholderStack>
                      ) : (
                        <>
                          <h1>{idea?.name}</h1>
                          <h5 style={{ marginTop: 0, color: "white" }}>
                            <span style={{ textTransform: "uppercase" }}>{t("generic.idea")}</span>
                            {idea.ownerChallenge && (
                              <span style={{ color: "white" }}>
                                {" "}
                                For{" "}
                                <Link style={{ color: "white" }} to={`/challenges/${idea.challenge}`}>
                                  {idea.ownerChallenge?.name}
                                </Link>
                              </span>
                            )}
                          </h5>
                          <div style={{ display: "flex", marginBottom: 25 }}>
                            {board.laneObject?.laneCount > 0 && util.canManageChallenge(user, idea.ownerChallenge) && (
                              <>
                                <LaneInfo board={board} idea={idea} style={{ marginRight: 10 }} />
                                <div style={{ borderLeft: "2px solid white", height: 24, marginRight: 10 }}></div>
                              </>
                            )}

                            {!loading ? (
                              idea.isSubmitted ? (
                                <Popup
                                  trigger={
                                    <Label style={{ maxHeight: "fit-content" }} color="green">
                                      Submitted
                                    </Label>
                                  }
                                  content={`Submitted ${moment(idea.submittedAt).format("DD/MM/YYYY HH:mm")}`}
                                />
                              ) : (
                                <Label style={{ maxHeight: "fit-content" }} color="yellow">
                                  Draft
                                </Label>
                              )
                            ) : null}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                              <span style={{ color: "white", marginRight: 5 }}>Created by </span>
                              {idea.lockedOwnerIdeaBusinessProfile ? (
                                <IdeaBusinessProfileChip
                                  ideaBusinessProfile={idea.lockedOwnerIdeaBusinessProfile}
                                  inverted
                                  style={{ marginRight: 15 }}
                                />
                              ) : null}
                              {idea.authors
                                ? idea.authors.map((a) => (
                                    <UserChip
                                      user={a}
                                      key={a._id}
                                      inverted
                                      style={{ marginRight: 15, marginBottom: 4 }}
                                    />
                                  ))
                                : null}
                              {idea?.stamps?.length ? (
                                <div style={{ marginTop: 5, display: "flex" }}>
                                  {idea.stamps.map((stamp, i) => (
                                    <Emoji key={i} emoji={{ id: stamp }} size={22} />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </PreviewHeader>
              <IdeaContainer>
                <BodyContainer>
                  {!theme.sizes.isComputer && canViewProjectBoard ? (
                    <PreviewSideWindow
                      idea={idea}
                      assessment={assessment}
                      onUpdateIdea={onUpdateIdea}
                      assessmentEnabled={assessmentEnabled}
                      lanes={lanes}
                      updateProjectLane={updateProjectLane}
                      onIdeaActivity={onIdeaActivity}
                      challenge={propChallenge}
                      fieldRefs={fieldRefs}
                      sectionRefs={sectionRefs}
                      scrollRef={previewContainerRef}
                    />
                  ) : null}
                  <IdeaBody
                    idea={idea ?? {}}
                    setSectionRef={setSectionRef}
                    sectionRefs={sectionRefs}
                    setFieldRef={setFieldRef}
                    fieldRefs={fieldRefs}
                    challenge={idea?.ownerChallenge}
                    loading={loading}
                    assessment={assessment}
                    updateAssessment={updateAssessment}
                    submitAssessment={submitAssessment}
                    assessmentIsSubmissable={assessmentIsSubmissable}
                    isPreview
                    isEditing={false}
                    scrollRef={previewContainerRef}
                    removeIdeaFromMerge={removeIdeaFromMerge}
                    removingIdeaFromMerge={removingIdeaFromMerge}
                    updateIdea={updateIdea}
                  />
                </BodyContainer>
              </IdeaContainer>
            </>
          )}
        </PreviewContainer>
      </ModalContainer>
    </SlidingModal>
  )
}

export const HandledIdeaPreview = ({
  ideaId,
  onUpdateIdea: propUpdateIdea = (_ideaId, _data) => {},
  removeIdeaFromMerge: propRemoveIdeaFromMerge = (
    mergeIdeaId: string,
    ideaId: string,
    onComplete = (_mergeIdeaId: string, _ideaId: string) => {},
    onError = (_mergeIdeaId: string, _ideaId: string) => {},
  ) => {},
  ...rest
}) => {
  const ideaQuery = useQuery({
    queryKey: ["idea", ideaId],
    queryFn: asQuery(api.ideas.get, {
      params: [ideaId, null],
    })!,
    enabled: !!ideaId,
    placeholderData: (prev) => prev,
  })

  const onUpdateIdea = useCallback(
    (data) => {
      api.queryClient.setQueryData(["idea", ideaId], (old: typeof ideaQuery.data) => {
        const newData = old ? { ...old, ...data } : data
        propUpdateIdea(ideaId, newData)
        return newData
      })
    },
    [ideaId, propUpdateIdea],
  )

  const removeIdeaFromMerge = useCallback(
    (mergeIdeaId, childIdeaId) => {
      propRemoveIdeaFromMerge(mergeIdeaId, childIdeaId, () => {
        if (mergeIdeaId === ideaId) {
          api.queryClient.setQueryData(["idea", ideaId], (old: typeof ideaQuery.data) => {
            if (!old) return old
            return {
              ...old,
              children: old.children.filter((c) => c !== childIdeaId),
              ownerChildren: old.ownerChildren.filter((c) => c._id !== childIdeaId),
            }
          })
        }
      })
    },
    [ideaId, propRemoveIdeaFromMerge],
  )

  return (
    <IdeaPreview
      isOpen={!!ideaId}
      idea={ideaQuery.data}
      onUpdateIdea={onUpdateIdea}
      loading={ideaQuery.isLoading}
      removeIdeaFromMerge={removeIdeaFromMerge}
      {...rest}
    />
  )
}

export default IdeaPreview
